/**
 * Create custom module — /settings/custom-modules/new
 *
 * Simple form with key/name/description/icon. On submit calls
 * modules.custom.modules.create and navigates to the new module's
 * editor. On first-ever create, the backend also seeds system roles
 * and auto-assigns the creator to custom:admin (transparent to the UI).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/new')({
  component: NewCustomModulePage,
})

function NewCustomModulePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [key, setKey] = useState('')
  // Track whether the user has manually edited the key field. Until
  // they do, the key auto-follows the name — slugified on every
  // keystroke. Once they type in the key field, we stop clobbering
  // it.
  const [keyDirty, setKeyDirty] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [icon, setIcon] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const createMutation = useMutation(
    orpc.modules.custom.modules.create.mutationOptions({
      onSuccess: (mod) => {
        toast.success(`Module "${mod.name}" created`)
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.modules.list.queryKey(),
        })
        void navigate({
          to: '/settings/custom-modules/$key',
          params: { key: mod.key },
        })
      },
      onError: (err) => {
        setFormError(err.message || 'Failed to create module')
      },
    }),
  )

  // Auto-suggest a key from the name (snake_case) until the user
  // manually edits the key field.
  const handleNameChange = (value: string) => {
    setName(value)
    if (!keyDirty) {
      const suggested = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 63)
      setKey(suggested)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    createMutation.mutate({
      key,
      name,
      description: description || undefined,
      icon: icon || undefined,
    })
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-2xl space-y-4">
      <Link
        to="/settings/custom-modules"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to modules
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>New custom module</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Food Safety"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Display name shown in menus and page headers.
              </p>
            </div>

            <div>
              <Label htmlFor="key">
                Key <span className="text-destructive">*</span>
              </Label>
              <Input
                id="key"
                value={key}
                onChange={(e) => {
                  setKey(e.target.value)
                  setKeyDirty(true)
                }}
                placeholder="food_safety"
                pattern="[a-z][a-z0-9_]*"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL-safe identifier. Lowercase letters, numbers, and underscores.
                Cannot be changed later.
              </p>
            </div>

            <div>
              <Label htmlFor="icon">Icon</Label>
              <Input
                id="icon"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="Box"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lucide icon name. Leave blank to use the default box icon.
              </p>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this module for?"
                rows={3}
              />
            </div>

            {formError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {formError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Link to="/settings/custom-modules">
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create module'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
